/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import emailjs from 'emailjs-com';
import config from '../../../../config';
import 'react-toastify/dist/ReactToastify.css';

export const ContactForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const toastifySuccess = () => {
    toast.success('✅ Message sent! We’ll get back to you shortly.', {
      position: 'bottom-right',
      autoClose: 4000,
      theme: 'dark',
    });
  };

  const onSubmit = async (data: any) => {
    const { name, company, email, phone, message } = data;
    setIsLoading(true);
    try {
      await emailjs.send(
        config.serviceId,
        config.templateId,
        { name, company, email, phone, message },
        config.userId,
      );
      reset();
      toastifySuccess();
    } catch (err) {
      toast.error('❌ Failed to send message. Please try again later.', { theme: 'dark' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <form
        id="contact-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="w-full grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {/* LEFT COLUMN */}
        <div className="flex flex-col space-y-6">
          {/* Name */}
          <div>
            <label className="block text-[#9EE7FF] mb-2 font-semibold">Name</label>
            <input
              type="text"
              placeholder="Enter your name"
              {...register('name', {
                required: 'Please enter your name',
                maxLength: { value: 50, message: 'Maximum 50 characters' },
              })}
              className="w-full p-3 rounded-xl bg-[#041421]/70 text-white border border-[#00D4FF]/30 
              focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/40 outline-none 
              transition-all duration-300"
            />
            {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>}
          </div>

          {/* Company Name (new input) */}
          <div>
            <label className="block text-[#9EE7FF] mb-2 font-semibold">Company Name</label>
            <input
              type="text"
              placeholder="Enter your company name"
              {...register('company', {
                required: 'Please enter your company name',
                maxLength: { value: 100, message: 'Maximum 100 characters' },
              })}
              className="w-full p-3 rounded-xl bg-[#041421]/70 text-white border border-[#00D4FF]/30 
              focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/40 outline-none 
              transition-all duration-300"
            />
            {errors.company && (
              <p className="text-red-400 text-sm mt-1">{errors.company.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-[#9EE7FF] mb-2 font-semibold">Email</label>
            <input
              type="email"
              placeholder="you@example.com"
              {...register('email', {
                required: 'Please enter your email',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
              })}
              className="w-full p-3 rounded-xl bg-[#041421]/70 text-white border border-[#00D4FF]/30 
              focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/40 outline-none 
              transition-all duration-300"
            />
            {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-[#9EE7FF] mb-2 font-semibold">Phone Number</label>
            <input
              type="tel"
              placeholder="e.g. 0821234567"
              {...register('phone', {
                required: 'Please enter your phone number',
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: 'Enter a valid 10-digit number',
                },
              })}
              className="w-full p-3 rounded-xl bg-[#041421]/70 text-white border border-[#00D4FF]/30 
              focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/40 outline-none 
              transition-all duration-300"
            />
            {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone.message}</p>}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col justify-between">
          {/* Message */}
          <div>
            <label className="block text-[#9EE7FF] mb-2 font-semibold">Message</label>
            <textarea
              rows={9}
              placeholder="Write your message here..."
              {...register('message', { required: 'Please enter a message' })}
              className="w-full p-3 rounded-xl bg-[#041421]/70 text-white border border-[#00D4FF]/30 
              focus:border-[#00D4FF] focus:ring-2 focus:ring-[#00D4FF]/40 outline-none 
              transition-all duration-300 resize-none"
            />
            {errors.message && (
              <p className="text-red-400 text-sm mt-1">{errors.message.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={isLoading}
              className={`px-12 py-3 rounded-full font-semibold text-black bg-[#00D4FF] 
              hover:bg-[#00b8e6] transition-all duration-300 shadow-[0_0_20px_rgba(0,212,255,0.4)] 
              ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {isLoading ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>

        <ToastContainer />
      </form>
    </div>
  );
};

export default ContactForm;
